import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { TakeittoLogo } from '../components/TakeittoLogo';
import { toast } from 'sonner';
import { Copy, Check, LogIn, ArrowLeft, Shield, Truck } from 'lucide-react';

const DEMO_ACCOUNTS = [
  {
    key: 'admin',
    role: 'Admin / Dispatcher',
    icon: Shield,
    email: 'admin@takeitto.com.au',
    password: 'admin123',
    description: 'Full dispatch dashboard, admin console, orders, drivers and Henry\u2019s sync.',
    accent: 'from-indigo-500 to-violet-600',
  },
  {
    key: 'driver',
    role: 'Driver (PWA)',
    icon: Truck,
    email: 'alex.sydney@takeitto.com.au',
    password: 'driver123',
    description: 'Driver app with assigned stops, optimized routes and proof-of-delivery photos.',
    accent: 'from-emerald-500 to-teal-600',
  },
];

export default function DemoAccess() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [copied, setCopied] = useState('');
  const [loggingIn, setLoggingIn] = useState('');

  const copy = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(id);
      setTimeout(() => setCopied(''), 1500);
    } catch {
      toast.error('Could not copy');
    }
  };

  const quickLogin = async (account) => {
    setLoggingIn(account.key);
    try {
      const user = await login(account.email, account.password);
      toast.success(`Signed in as ${account.role}`);
      if (user.role === 'driver') navigate('/driver');
      else navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Demo login failed');
    } finally {
      setLoggingIn('');
    }
  };

  const Field = ({ label, value, id }) => (
    <div className="flex items-center justify-between gap-2 bg-gray-50 rounded-lg px-3 py-2">
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wide text-gray-400">{label}</p>
        <p className="text-sm font-mono text-gray-800 truncate">{value}</p>
      </div>
      <button
        onClick={() => copy(value, id)}
        className="shrink-0 p-1.5 rounded-md hover:bg-gray-200 text-gray-500"
        data-testid={`copy-${id}`}
        title="Copy"
      >
        {copied === id ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center py-10 px-4" data-testid="demo-access-page">
      <div className="w-full max-w-2xl">
        <div className="flex items-center justify-between mb-8">
          <TakeittoLogo size="md" />
          <button
            onClick={() => navigate('/login')}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-indigo-600"
            data-testid="back-to-login"
          >
            <ArrowLeft className="w-4 h-4" /> Back to login
          </button>
        </div>

        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Demo Access</h1>
          <p className="text-gray-500 text-sm mt-1">
            Use these credentials to show the platform. Copy them, or tap "Log in" to jump straight in.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {DEMO_ACCOUNTS.map((acc) => {
            const Icon = acc.icon;
            return (
              <div key={acc.key} className="bg-white rounded-2xl border shadow-sm overflow-hidden" data-testid={`demo-card-${acc.key}`}>
                <div className={`bg-gradient-to-r ${acc.accent} px-4 py-3 flex items-center gap-2 text-white`}>
                  <Icon className="w-5 h-5" />
                  <span className="font-semibold">{acc.role}</span>
                </div>
                <div className="p-4 space-y-3">
                  <p className="text-xs text-gray-500">{acc.description}</p>
                  <Field label="Email" value={acc.email} id={`${acc.key}-email`} />
                  <Field label="Password" value={acc.password} id={`${acc.key}-password`} />
                  <Button
                    onClick={() => quickLogin(acc)}
                    disabled={loggingIn === acc.key}
                    className="w-full bg-gray-900 hover:bg-gray-800"
                    data-testid={`demo-login-${acc.key}`}
                  >
                    <LogIn className="w-4 h-4 mr-2" />
                    {loggingIn === acc.key ? 'Signing in...' : `Log in as ${acc.key === 'admin' ? 'Admin' : 'Driver'}`}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-center text-xs text-gray-400 mt-8">
          These are shared demo accounts. Data may be reset periodically.
        </p>
      </div>
    </div>
  );
}
